import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import PageTitle from 'components/UI/PageTitle';
import CreateFollowUpConfig from 'pages/FollowUpConfig/CreateFollowUpConfig';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import { useGetFollowUpsQuery } from 'services/api/followUpConfig.api';

import FollowUpConfigTableRow from './components/FollowUpConfigTableRow';

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`follow-up-tabpanel-${index}`}
      aria-labelledby={`follow-up-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const FollowUpConfigEdit = () => {
  const { businessUnitId } = useParams();
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { data: businessUnit } = useGetBusinessUnitQuery(Number(businessUnitId), {
    skip: !businessUnitId,
  });
  const businessUnitName = businessUnit?.name ?? '';

  const { data: validFollowUps, refetch: refetchValidFollowUps } = useGetFollowUpsQuery(
    { businessUnitId: Number(businessUnitId), isArchived: false },
    {
      // Refetch on mount/reconnect, but use cache otherwise
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: true,
      skip: !businessUnitId,
    }
  );

  const { data: archivedFollowUps, refetch: refetchArchivedFollowUps } = useGetFollowUpsQuery(
    { businessUnitId: Number(businessUnitId), isArchived: true },
    {
      // Refetch on mount/reconnect, but use cache otherwise
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: true,
      skip: !businessUnitId,
    }
  );

  // Optimized refetch - refetch both tabs when archiving/unarchiving
  const handleRefetch = useCallback(() => {
    refetchValidFollowUps().catch((error) => {
      console.error('Error refetching follow-ups:', error);
    });

    refetchArchivedFollowUps().catch((error) => {
      console.error('Error refetching archived follow-ups:', error);
    });
  }, [refetchValidFollowUps, refetchArchivedFollowUps]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!businessUnitId) {
    return <div>BusinessUnit id not found</div>;
  }

  const currentFollowUps = activeTab === 0 ? validFollowUps : archivedFollowUps;
  const emptyMessage = activeTab === 0 ? 'No hay follow ups configurados' : 'No hay follow ups archivados';

  const renderTable = () => {
    if (!currentFollowUps || currentFollowUps.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
            }}
          >
            {emptyMessage}
          </Typography>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '50px', minWidth: '50px' }} />
              <TableCell sx={{ width: '80px', minWidth: '80px' }}>ID</TableCell>
              <TableCell sx={{ width: '200px', minWidth: '200px' }}>
                <Tooltip title="Identificador único del follow up para referenciarlo en el sistema">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <span>Identificador</span>
                    <InfoIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: '150px', minWidth: '150px' }}>
                <Tooltip title="Rango de horas del día en que se pueden enviar los mensajes (formato 24 horas)">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <span>Horas de Trabajo</span>
                    <InfoIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: '180px', minWidth: '180px' }}>
                <Tooltip title="Tiempo mínimo y máximo de espera en minutos antes de enviar el follow up">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <span>Tiempo de Espera (min)</span>
                    <InfoIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ width: '120px', minWidth: '120px' }}>
                <Tooltip title="Indica si el follow up se puede enviar durante los fines de semana">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <span>Fin de Semana</span>
                    <InfoIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ width: '100px', minWidth: '100px' }}>
                <Tooltip title="Estado actual del follow up: Activo (habilitado) o Inactivo (deshabilitado)">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <span>Estado</span>
                    <InfoIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: '150px', minWidth: '150px' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentFollowUps.map((followUp) => (
              <FollowUpConfigTableRow key={followUp.id} followUpConfig={followUp} onUpdate={handleRefetch} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ px: 2, pt: 2, pb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <PageTitle>Follow Ups{businessUnitName && ` - ${businessUnitName}`}</PageTitle>
        <PrimaryBtn sx={{ ml: 2 }} onClick={() => navigate(-1)} variant="contained">
          Volver
        </PrimaryBtn>
        <PrimaryBtn
          sx={{ ml: 2 }}
          onClick={() => setShowCreateForm(!showCreateForm)}
          variant="contained"
          color="secondary"
        >
          {showCreateForm ? 'Cancelar' : 'Crear Nuevo'}
        </PrimaryBtn>
      </Box>
      {showCreateForm && (
        <CreateFollowUpConfig
          businessUnitId={Number(businessUnitId)}
          onSuccess={() => {
            setShowCreateForm(false);
            // Cache invalidation will automatically refetch, but we can still call it for immediate UI update
            handleRefetch();
          }}
        />
      )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="follow-up tabs">
          <Tab label="Follow Ups" id="follow-up-tab-0" aria-controls="follow-up-tabpanel-0" />
          <Tab label="Archivados" id="follow-up-tab-1" aria-controls="follow-up-tabpanel-1" />
        </Tabs>
      </Box>
      <TabPanel value={activeTab} index={0}>
        {renderTable()}
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        {renderTable()}
      </TabPanel>
    </Box>
  );
};

export default FollowUpConfigEdit;
